import { z } from 'zod';

import { colorChoiceSchema, optionalPxSchema } from '@/features/storefront/colorChoice';
import {
  ANCHOR_SECTION_TYPES,
  SECTION_DEVICES,
  elementBase,
  newElementId,
  optionalText,
  type AnchorSectionType,
} from '@/features/storefront/sections/primitives';

/**
 * A page's own content, taken apart.
 *
 * A section is a band of a page and an anchor is the band that *is* the page -
 * the product, the basket, the till. Until now an anchor was opaque: a
 * merchant could stand things above and below it and say a few words about how
 * dense its grid was, but the title, the price, the buy button and the gallery
 * inside it were the theme's business and nobody else's.
 *
 * A part is one of those pieces, promoted to something the merchant holds. The
 * shape is deliberately the same one they already know from sections and
 * blocks - an ordered list of identified things, each of which can be hidden,
 * moved, styled and thrown away - because a merchant who has arranged their
 * home page has already learnt this feature, and the third level of the tree
 * should cost them nothing new.
 *
 * Three things keep that from becoming a way to break a shop:
 *
 * - **The set is closed.** A part type is one of {@link PART_TYPES} and nothing
 *   else. A document naming a part this build does not understand loses that
 *   part rather than rendering a hole.
 * - **A region is the theme's, not the merchant's.** Parts stand in named
 *   regions - the gallery column, the buy column, the band underneath - and the
 *   theme decides what those regions look like. Moving the price into the
 *   gallery column moves it into *this theme's* gallery column, so the shop
 *   still reads as the shop.
 * - **Empty means untouched.** An anchor whose `parts` is `[]` renders exactly
 *   what it rendered before parts existed. The list is written in full the
 *   first time a merchant changes anything, and never before.
 *
 * Editing lives in `features/store` and rendering in `features/storefront`, and
 * both read this file: two definitions of a part would drift, and the way that
 * drift shows up is a preview that lies about the live page.
 */

// --- The closed set -------------------------------------------------------
/**
 * Every component a merchant may place, across every page.
 *
 * One flat list rather than one per page, mirrored by `PART_TYPES` in
 * `app/schemas/store.py`. Which page each belongs on is {@link PartSpec.anchors}
 * below - data, so the library, the renderer and the validator all read the
 * same answer.
 */
export const PART_TYPES = [
  // Anywhere: the merchant's own words and furniture.
  'custom_heading',
  'custom_text',
  'custom_button',
  'custom_image',
  'divider',
  'spacer',

  // The product page.
  'product_breadcrumbs',
  'product_gallery',
  'product_brand',
  'product_title',
  'product_price',
  'product_compare_price',
  'product_tax_note',
  'product_sku',
  'product_availability',
  'product_short_description',
  'product_highlights',
  'product_variants',
  'product_quantity',
  'product_add_to_cart',
  'product_buy_now',
  'product_buy_on_whatsapp',
  'product_description',
  'product_specs',
  'product_shipping_info',
  'product_payment',
  'product_share',
  'product_related',
  'product_recommendations',
  'product_comparison',
  'product_accordion',
  'product_delivery_estimate',
  'product_stock_bar',
  'product_offers',
  'product_sticky_cart',
  'product_size_chart',
  'product_bundle',
  'product_qa',
  'product_notify_me',
  'product_seller_info',

  // The cart.
  'cart_heading',
  'cart_items',
  'cart_continue_link',
  'cart_summary_heading',
  'cart_subtotal',
  'cart_discount',
  'cart_tax',
  'cart_shipping',
  'cart_total',
  'cart_coupon',
  'cart_checkout_button',
  'cart_recommendations',
  'cart_note',

  // The till.
  'checkout_heading',
  'checkout_fields',
  'checkout_summary_heading',
  'checkout_summary_items',
  'checkout_subtotal',
  'checkout_discount',
  'checkout_tax',
  'checkout_shipping',
  'checkout_total',
  'checkout_coupon',
  'checkout_gift_card',
  'checkout_place_order',

  // The catalog.
  'listing_heading',
  'listing_refine_link',
  'listing_banner',
  'listing_filters',
  'listing_sort',
  'listing_grid',
  'listing_promo_tile',
  'listing_pagination',

  // The category index.
  'category_heading',
  'category_grid',

  // Search.
  'search_heading',
  'search_count',
  'search_facets',
  'search_sort',
  'search_grid',
  'search_pagination',
] as const;

export type PartType = (typeof PART_TYPES)[number];

export function isPartType(value: string): value is PartType {
  return (PART_TYPES as readonly string[]).includes(value);
}

// --- Presentation ---------------------------------------------------------
/**
 * Every presentational choice below offers "the theme's own" first and starts
 * there, for the same reason an anchor's settings do: a merchant who moved
 * their buy button has not thereby asked for a different typeface on it, and a
 * shop whose merchant never opened the style group has to render exactly what
 * the theme designed.
 */
const THEME = 'theme';

export const PART_ALIGNMENTS = [THEME, 'left', 'center', 'right'] as const;
export const PART_SPACES = [THEME, 'none', 'xs', 'sm', 'md', 'lg', 'xl'] as const;
export const PART_TEXT_SIZES = [
  THEME,
  'xs',
  'sm',
  'base',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
] as const;
export const PART_TEXT_WEIGHTS = [THEME, 'normal', 'medium', 'semibold', 'bold'] as const;
export const PART_TEXT_CASES = [THEME, 'none', 'uppercase', 'capitalize'] as const;
/**
 * Backgrounds name the shop's own roles rather than free colours: a part
 * painted `#f3f3f3` is a part that stops matching the moment the merchant
 * changes their palette, and every one of these follows it.
 */
export const PART_BACKGROUNDS = [THEME, 'none', 'surface', 'primary'] as const;
/** How wide a part stands inside its region. */
export const PART_WIDTHS = [THEME, 'auto', 'full'] as const;

export type PartAlignment = (typeof PART_ALIGNMENTS)[number];
export type PartSpace = (typeof PART_SPACES)[number];
export type PartTextSize = (typeof PART_TEXT_SIZES)[number];
export type PartTextWeight = (typeof PART_TEXT_WEIGHTS)[number];
export type PartTextCase = (typeof PART_TEXT_CASES)[number];
export type PartBackground = (typeof PART_BACKGROUNDS)[number];
export type PartWidth = (typeof PART_WIDTHS)[number];

/**
 * What one component-specific setting may be.
 *
 * Scalars only, and read through {@link PART_SPECS} rather than trusted: the
 * option map is the one open-ended field on a part, so a value whose key this
 * build does not know, or whose type does not match what the option was
 * declared as, never reaches the renderer. That keeps "a newer deploy saved an
 * option we have not shipped yet" a part that renders with its defaults instead
 * of a part that renders wrong.
 */
export const partOptionValueSchema = z.union([z.string(), z.number(), z.boolean()]);
export type PartOptionValue = z.infer<typeof partOptionValueSchema>;

/**
 * A part's colour: blank for the theme's own, one of the shop's roles, or a hex
 * the merchant picked. Anything else parses to blank, which is the theme's.
 */
export const PART_COLOR_ROLES = ['ink', 'muted', 'primary', 'background', 'surface'] as const;
export type PartColorRole = (typeof PART_COLOR_ROLES)[number];

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

function readColor(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '';
  if ((PART_COLOR_ROLES as readonly string[]).includes(trimmed)) return trimmed;
  return HEX.test(trimmed) ? trimmed.toLowerCase() : '';
}

export const pagePartSchema = z.object({
  ...elementBase,
  type: z.enum(PART_TYPES),
  /**
   * Which of the page's regions this part stands in.
   *
   * A free string rather than an enum because the regions differ per page, and
   * a union of every page's regions would let a document put a cart's totals
   * panel on a product page's gallery rail. It is narrowed where it is read
   * instead - see {@link partsInRegion} - so a region this page does not have
   * puts its part in the main one rather than losing it.
   */
  region: z.string().max(32).nullish().transform((value) => value ?? ''),
  /** The screens this part is left off. Empty - the default - is every screen. */
  hidden_on: z.array(z.enum(SECTION_DEVICES)).default([]),
  /**
   * The merchant's own words for it: a button's label, a heading's text, the
   * name over a totals row. Blank keeps whatever the shop already says, which
   * is what makes this safe to offer on parts whose words are translated.
   */
  label: optionalText,
  /** A paragraph, for the parts that are a paragraph. */
  body: optionalText,
  align: z.enum(PART_ALIGNMENTS).default(THEME),
  space_above: z.enum(PART_SPACES).default(THEME),
  space_below: z.enum(PART_SPACES).default(THEME),
  text_size: z.enum(PART_TEXT_SIZES).default(THEME),
  text_weight: z.enum(PART_TEXT_WEIGHTS).default(THEME),
  text_case: z.enum(PART_TEXT_CASES).default(THEME),
  /** Blank is the theme's own colour. See {@link readColor}. */
  text_color: z.string().nullish().transform(readColor),
  background: z.enum(PART_BACKGROUNDS).default(THEME),
  /**
   * A colour of the merchant's own behind the part, over `background`, and
   * exact padding around it. Optional - unset is the part as its presets draw
   * it - so every part saved before these existed renders unchanged.
   */
  background_color: colorChoiceSchema,
  padding_top_px: optionalPxSchema,
  padding_bottom_px: optionalPxSchema,
  padding_left_px: optionalPxSchema,
  padding_right_px: optionalPxSchema,
  width: z.enum(PART_WIDTHS).default(THEME),
  /** Component-specific settings, declared per type in {@link PART_LIBRARY}. */
  options: z.record(partOptionValueSchema).nullish().transform((value) => value ?? {}),
});

export type PagePart = z.infer<typeof pagePartSchema>;

// --- Regions --------------------------------------------------------------
/**
 * The places a part may stand on one page.
 *
 * A region is the theme's structure, named so a merchant can aim at it: the
 * gallery column, the buy column, the band under both. It is not a layout the
 * merchant builds - the theme still decides that a product page is two columns
 * and how wide each is - which is exactly how a shop keeps its design language
 * while its owner rearranges what is inside it.
 *
 * The first region of a page is its main one, and is where a part with no
 * region - or with one this page does not have - is put.
 */
export interface RegionSpec {
  id: string;
  label: string;
  description: string;
}

export const ANCHOR_REGIONS: Record<AnchorSectionType, readonly RegionSpec[]> = {
  product_details: [
    {
      id: 'top',
      label: 'Above the product',
      description: 'Full width, over both columns - where a breadcrumb or a notice stands.',
    },
    {
      id: 'main',
      label: 'Buy column',
      description: 'Beside the pictures: the name, the price and the way into the basket.',
    },
    {
      id: 'gallery',
      label: 'Gallery column',
      description: 'The pictures, and anything you want read alongside them.',
    },
    { id: 'below', label: 'Below the product', description: 'Full width, under both columns.' },
  ],
  product_list: [
    { id: 'top', label: 'Above the catalog', description: 'The heading and the filters.' },
    { id: 'main', label: 'The catalog', description: 'The grid of products and its pages.' },
    { id: 'below', label: 'Below the catalog', description: 'Under the grid, full width.' },
  ],
  category_list: [
    { id: 'top', label: 'Above the shelves', description: 'The heading.' },
    { id: 'main', label: 'The shelves', description: 'The grid of categories.' },
    { id: 'below', label: 'Below the shelves', description: 'Under the grid.' },
  ],
  search_results: [
    { id: 'top', label: 'Above the results', description: 'The heading, the count and the sort.' },
    { id: 'facets', label: 'Facet panel', description: 'The rail a shopper narrows results with.' },
    { id: 'main', label: 'The results', description: 'The grid of matches and its pages.' },
    { id: 'below', label: 'Below the results', description: 'Under the grid.' },
  ],
  cart_lines: [
    { id: 'top', label: 'Above the basket', description: 'Full width, over both columns.' },
    { id: 'main', label: 'The lines', description: 'What is in the cart.' },
    {
      id: 'summary',
      label: 'Totals panel',
      description: 'Beside the lines: the money and the way out.',
    },
    { id: 'below', label: 'Below the basket', description: 'Under both, full width.' },
  ],
  checkout_form: [
    { id: 'top', label: 'Above the till', description: 'Full width, over both columns.' },
    { id: 'main', label: 'The form', description: 'Address, delivery and payment.' },
    { id: 'summary', label: 'Order summary', description: 'Beside the form: what is being bought.' },
    { id: 'below', label: 'Below the till', description: 'Under both, full width.' },
  ],
  customer_login: [{ id: 'main', label: 'The page', description: 'The sign-in form and its words.' }],
  customer_register: [
    { id: 'main', label: 'The page', description: 'The registration form and its words.' },
  ],
  customer_account: [{ id: 'main', label: 'The page', description: "A shopper's own page." }],
  blog_index: [{ id: 'main', label: 'The page', description: 'The list of posts.' }],
  blog_post: [{ id: 'main', label: 'The page', description: 'One post.' }],
  team_member_details: [{ id: 'main', label: 'The page', description: 'One person.' }],
  not_found: [{ id: 'main', label: 'The page', description: 'The dead end.' }],
};

/**
 * The regions of a page, narrowed to the ones the theme draws.
 *
 * `drawn` is left off almost everywhere: a theme that arranges the same regions
 * differently has not changed which regions exist. It is passed by the editor
 * and by the defaults for the one theme whose page is art-directed - see
 * `ThemeAnchorParts.regions` - so a merchant is never shown a region their
 * theme does not paint.
 *
 * Never empty: a theme naming regions none of which exist has said something
 * that cannot be true, and the page's own list is the safer answer.
 */
export function anchorRegions(
  anchor: AnchorSectionType,
  drawn?: readonly string[],
): readonly RegionSpec[] {
  const all = ANCHOR_REGIONS[anchor] ?? ANCHOR_REGIONS.product_details;
  if (!drawn) return all;
  const kept = all.filter((region) => drawn.includes(region.id));
  return kept.length > 0 ? kept : all;
}

/** A page's main region: where a part with nowhere else to go is put. */
export function defaultRegion(anchor: AnchorSectionType): string {
  return anchorRegions(anchor)[0].id;
}

/** One region's description, for a heading over a list of its parts. */
export function regionSpec(anchor: AnchorSectionType, region: string): RegionSpec {
  return anchorRegions(anchor).find((each) => each.id === region) ?? anchorRegions(anchor)[0];
}

// --- The registry ---------------------------------------------------------
/** Which shelf of the "add component" library a part is offered on. */
export const PART_GROUPS = [
  'essential',
  'detail',
  'action',
  'money',
  'discovery',
  'custom',
] as const;
export type PartGroup = (typeof PART_GROUPS)[number];

export const PART_GROUP_LABELS: Record<PartGroup, string> = {
  essential: 'The page itself',
  detail: 'Details',
  action: 'Buttons and choices',
  money: 'Money',
  discovery: 'More to find',
  custom: 'Your own',
};

/** One component-specific setting, and the control the inspector draws for it. */
export type PartOptionSpec =
  | { key: string; label: string; hint?: string; kind: 'switch'; default: boolean }
  | {
      key: string;
      label: string;
      hint?: string;
      kind: 'select';
      choices: readonly { value: string; label: string }[];
      default: string;
    }
  | {
      key: string;
      label: string;
      hint?: string;
      kind: 'number';
      min: number;
      max: number;
      default: number;
    }
  | { key: string; label: string; hint?: string; kind: 'text'; placeholder?: string; default: string };

/**
 * Which of the shared style controls a part is offered.
 *
 * Not every control means something on every part: a divider has no typeface,
 * and a grid of products has no single colour to set. An inspector that offered
 * all of them everywhere would be a panel a merchant has to learn to ignore, so
 * each part names the ones that do something.
 */
export interface PartSupports {
  /** A words field - a heading's text, a button's label, a row's name. */
  label?: boolean;
  /** A paragraph field. */
  body?: boolean;
  align?: boolean;
  typography?: boolean;
  color?: boolean;
  background?: boolean;
  width?: boolean;
}

export interface PartSpec {
  type: PartType;
  label: string;
  description: string;
  group: PartGroup;
  /**
   * The pages this part belongs on. `'any'` is the merchant's own furniture - a
   * heading, a paragraph, a rule - which is welcome anywhere.
   */
  anchors: readonly AnchorSectionType[] | 'any';
  /**
   * The regions it may stand in, first being where it lands when added. Left
   * off means every region the page has, which is right for the merchant's own
   * furniture and wrong for a facet panel.
   */
  regions?: readonly string[];
  /** At most one per page. True for everything that *is* the page. */
  unique?: boolean;
  /**
   * A part the page does not work without: the grid on a catalog, the fields at
   * the till. It may be moved and styled, never removed or hidden - the same
   * bargain the anchor itself strikes with the page.
   */
  required?: boolean;
  supports?: PartSupports;
  options?: readonly PartOptionSpec[];
  /** What it arrives as, over the schema's own defaults. */
  defaults?: Partial<Omit<PagePart, 'id' | 'type'>>;
  /** The placeholder the words field shows: what the shop says when it is blank. */
  labelPlaceholder?: string;
}

const TEXT_ONLY: PartSupports = { align: true, typography: true, color: true };
const BUTTON: PartSupports = { label: true, align: true, typography: true, width: true };

/** Choices offered on more than one part, written once. */
const GALLERY_ASPECTS = [
  { value: 'theme', label: "The theme's own" },
  { value: 'square', label: 'Square' },
  { value: 'portrait', label: 'Portrait' },
  { value: 'wide', label: 'Wide' },
] as const;

const GALLERY_THUMBS = [
  { value: 'theme', label: "The theme's own" },
  { value: 'below', label: 'Under the picture' },
  { value: 'rail', label: 'In a rail beside it' },
] as const;

/** How the product's pictures are laid out, as a whole. */
export const GALLERY_STYLES = [
  { value: 'carousel', label: 'One picture with a strip' },
  { value: 'thumbs_left', label: 'Thumbnail rail on the left' },
  { value: 'thumbs_bottom', label: 'Thumbnails underneath' },
  { value: 'grid', label: 'Every picture, two to a row' },
] as const;

/**
 * Every part, described for the merchant who is choosing it.
 *
 * The order within a page is the order the library offers them in, which is
 * roughly the order they stand on the page - a merchant scanning for "price"
 * finds it where they would expect it to be.
 */
export const PART_LIBRARY: readonly PartSpec[] = [
  // --- The merchant's own furniture, on any page ---------------------------
  {
    type: 'custom_heading',
    label: 'Heading',
    description: "A line of your own, in the shop's heading face.",
    group: 'custom',
    anchors: 'any',
    supports: { label: true, ...TEXT_ONLY },
    defaults: { label: 'Heading' },
    options: [
      {
        key: 'level',
        label: 'Heading level',
        hint: 'What it means to a screen reader and to a search engine.',
        kind: 'select',
        choices: [
          { value: 'h2', label: 'Section heading (h2)' },
          { value: 'h3', label: 'Sub-heading (h3)' },
          { value: 'p', label: 'Not a heading' },
        ],
        default: 'h2',
      },
    ],
  },
  {
    type: 'custom_text',
    label: 'Text',
    description: 'A paragraph of your own - a promise, a note, a warning.',
    group: 'custom',
    anchors: 'any',
    // Background as well as the text controls: a standing offer or a warning is
    // a paragraph that has to be seen as a box rather than read as prose, and
    // `partStyle` already paints one - it was simply never offered here.
    supports: { body: true, background: true, ...TEXT_ONLY },
    defaults: { body: 'Say something here.' },
  },
  {
    type: 'custom_button',
    label: 'Button',
    description: 'A link that looks like a button - a size guide, a contact page.',
    group: 'custom',
    anchors: 'any',
    supports: BUTTON,
    defaults: { label: 'Learn more' },
    options: [
      {
        key: 'href',
        label: 'Links to',
        kind: 'text',
        placeholder: '/pages/size-guide',
        default: '',
      },
      {
        key: 'style',
        label: 'Style',
        kind: 'select',
        choices: [
          { value: 'solid', label: 'Filled' },
          { value: 'outline', label: 'Outlined' },
          { value: 'link', label: 'A plain link' },
        ],
        default: 'outline',
      },
      {
        key: 'new_tab',
        label: 'Open in a new tab',
        hint: 'For an address away from your shop.',
        kind: 'switch',
        default: false,
      },
    ],
  },
  {
    type: 'custom_image',
    label: 'Image',
    description: 'A picture of your own - a size chart, a badge, a care label.',
    group: 'custom',
    anchors: 'any',
    supports: { align: true },
    options: [
      { key: 'image_url', label: 'Image', kind: 'text', default: '' },
      {
        key: 'alt',
        label: 'Alt text',
        hint: 'What it shows, for a shopper who cannot see it.',
        kind: 'text',
        default: '',
      },
      { key: 'href', label: 'Links to', kind: 'text', placeholder: '/pages/size-guide', default: '' },
      {
        key: 'max_width',
        label: 'Widest it gets',
        hint: 'In pixels. Zero leaves it to the column.',
        kind: 'number',
        min: 0,
        max: 1200,
        default: 0,
      },
    ],
  },
  {
    type: 'divider',
    label: 'Divider',
    description: 'A hairline rule, for separating one thing from the next.',
    group: 'custom',
    anchors: 'any',
    supports: {},
  },
  {
    type: 'spacer',
    label: 'Space',
    description: 'Empty room, when two things are standing too close.',
    group: 'custom',
    anchors: 'any',
    supports: {},
    options: [
      { key: 'height', label: 'Height', hint: 'In pixels.', kind: 'number', min: 4, max: 200, default: 32 },
    ],
  },

  // --- The product page ---------------------------------------------------
  {
    type: 'product_breadcrumbs',
    label: 'Breadcrumbs',
    description: 'Where the shopper is: the way back to the shop, and to the catalog.',
    group: 'detail',
    anchors: ['product_details'],
    // Two rungs and a name, so it belongs over the columns rather than inside
    // one of them - but a theme with no band above the product can still put it
    // at the top of either.
    regions: ['top', 'main', 'gallery'],
    unique: true,
    supports: TEXT_ONLY,
    options: [
      { key: 'home_label', label: 'First rung', kind: 'text', placeholder: 'Home', default: 'Home' },
      {
        key: 'catalog_label',
        label: 'Second rung',
        hint: 'Leave it blank to go straight from the shop to this product.',
        kind: 'text',
        placeholder: 'All products',
        default: 'All products',
      },
      {
        key: 'show_current',
        label: 'End on the product’s name',
        kind: 'switch',
        default: true,
      },
    ],
  },
  {
    type: 'product_gallery',
    label: 'Product images',
    description: 'The pictures, and the strip that picks between them.',
    group: 'essential',
    anchors: ['product_details'],
    regions: ['gallery', 'main', 'below'],
    unique: true,
    supports: { align: true },
    options: [
      {
        key: 'style',
        label: 'Layout',
        hint: 'One picture with a strip keeps the thumbnails setting below; the others decide it themselves.',
        kind: 'select',
        choices: GALLERY_STYLES,
        default: 'carousel',
      },
      { key: 'aspect', label: 'Picture shape', kind: 'select', choices: GALLERY_ASPECTS, default: 'theme' },
      { key: 'thumbs', label: 'Thumbnails', kind: 'select', choices: GALLERY_THUMBS, default: 'theme' },
      { key: 'rounded', label: 'Rounded corners', kind: 'switch', default: true },
      {
        key: 'sticky',
        label: 'Stay in view while the page scrolls',
        hint: 'On a computer screen only. A phone shows the pictures above everything else.',
        kind: 'switch',
        default: false,
      },
    ],
  },
  {
    type: 'product_brand',
    label: 'Brand',
    description: "The maker's name, above the product's own.",
    group: 'detail',
    anchors: ['product_details'],
    unique: true,
    supports: TEXT_ONLY,
  },
  {
    type: 'product_title',
    label: 'Product title',
    description: "The product's name. The page's one h1.",
    group: 'essential',
    anchors: ['product_details'],
    unique: true,
    required: true,
    supports: TEXT_ONLY,
  },
  {
    type: 'product_price',
    label: 'Price',
    description: 'What the chosen variant costs, and what it used to.',
    group: 'money',
    anchors: ['product_details'],
    unique: true,
    supports: TEXT_ONLY,
    options: [
      { key: 'show_compare_at', label: 'Show the was-price beside it', kind: 'switch', default: true },
      {
        key: 'show_saving',
        label: 'Show what they save',
        hint: 'A "Save 20%" beside the price, whenever there is a was-price to compare with.',
        kind: 'switch',
        default: false,
      },
    ],
  },
  {
    type: 'product_compare_price',
    label: 'Compare-at price',
    description: 'The was-price on its own, for putting somewhere else on the page.',
    group: 'money',
    anchors: ['product_details'],
    unique: true,
    supports: TEXT_ONLY,
    labelPlaceholder: 'Was',
    options: [{ key: 'show_label', label: 'Show a word before it', kind: 'switch', default: false }],
  },
  {
    type: 'product_tax_note',
    label: 'Tax note',
    description: 'The line under the price about what is included.',
    group: 'money',
    anchors: ['product_details'],
    unique: true,
    supports: TEXT_ONLY,
  },
  {
    type: 'product_sku',
    label: 'SKU',
    description: "The chosen variant's code, for a shopper who is ordering by it.",
    group: 'detail',
    anchors: ['product_details'],
    unique: true,
    supports: TEXT_ONLY,
    labelPlaceholder: 'SKU',
  },
  {
    type: 'product_availability',
    label: 'Availability',
    description: 'Whether the chosen variant is in stock.',
    group: 'detail',
    anchors: ['product_details'],
    unique: true,
    supports: TEXT_ONLY,
    options: [
      { key: 'in_stock_text', label: 'When it is in stock', kind: 'text', placeholder: 'In stock', default: '' },
      { key: 'out_of_stock_text', label: 'When it is not', kind: 'text', placeholder: 'Out of stock', default: '' },
      { key: 'show_dot', label: 'Show a coloured dot', kind: 'switch', default: true },
    ],
  },
  {
    type: 'product_short_description',
    label: 'Summary',
    description: "The product's one-line summary, above the options.",
    group: 'detail',
    anchors: ['product_details'],
    unique: true,
    supports: TEXT_ONLY,
  },
  {
    type: 'product_highlights',
    label: 'Highlights',
    description: 'A row of short claims beside the price - "Eco-certified", "15-year warranty".',
    group: 'detail',
    anchors: ['product_details'],
    unique: true,
    supports: { body: true, align: true, typography: true, color: true },
    defaults: { body: 'Modern\nEco-certified\n15-year warranty' },
    options: [
      {
        key: 'style',
        label: 'Shown as',
        kind: 'select',
        choices: [
          { value: 'chips', label: 'Outlined chips' },
          { value: 'ticks', label: 'A ticked list' },
          { value: 'icons', label: 'Icons over small labels' },
        ],
        default: 'chips',
      },
    ],
  },
  {
    type: 'product_variants',
    label: 'Options',
    description: 'Size, colour - the choice a shopper makes before buying.',
    group: 'action',
    anchors: ['product_details'],
    unique: true,
    supports: { label: true, align: true },
    labelPlaceholder: 'Options',
    options: [
      {
        key: 'style',
        label: 'Shown as',
        kind: 'select',
        choices: [
          { value: 'buttons', label: 'Buttons, one per combination' },
          { value: 'axes', label: 'One row per option' },
          { value: 'swatches', label: 'One row per option, colours as dots' },
          { value: 'dropdown', label: 'A dropdown' },
        ],
        default: 'buttons',
      },
      { key: 'hide_sold_out', label: 'Hide sold-out options', kind: 'switch', default: false },
    ],
  },
  {
    type: 'product_quantity',
    label: 'Quantity',
    description: 'How many, for a shop where people buy more than one.',
    group: 'action',
    anchors: ['product_details'],
    unique: true,
    supports: { label: true, align: true },
    labelPlaceholder: 'Quantity',
    options: [
      { key: 'max', label: 'Most they may buy at once', kind: 'number', min: 1, max: 99, default: 10 },
    ],
  },
  {
    type: 'product_add_to_cart',
    label: 'Add to cart',
    description: 'The button that puts the chosen variant in the basket.',
    group: 'action',
    anchors: ['product_details'],
    unique: true,
    supports: BUTTON,
    labelPlaceholder: 'Add to cart',
  },
  {
    type: 'product_buy_now',
    label: 'Buy now',
    description: 'Straight to the till, for a shopper who has already decided.',
    group: 'action',
    anchors: ['product_details'],
    unique: true,
    supports: BUTTON,
    labelPlaceholder: 'Buy now',
    options: [
      {
        key: 'style',
        label: 'Style',
        kind: 'select',
        choices: [
          { value: 'outline', label: 'Outlined' },
          { value: 'solid', label: 'Filled' },
        ],
        default: 'outline',
      },
    ],
  },
  {
    type: 'product_buy_on_whatsapp',
    label: 'Buy on WhatsApp',
    description: 'A chat with you about this product, opened with it already named.',
    group: 'action',
    anchors: ['product_details'],
    unique: true,
    supports: { label: true, align: true, width: true },
    labelPlaceholder: 'Buy on WhatsApp',
    options: [
      { key: 'show_icon', label: 'Show the WhatsApp mark', kind: 'switch', default: true },
    ],
  },
  {
    type: 'product_description',
    label: 'Description',
    description: "The product's full description.",
    group: 'detail',
    anchors: ['product_details'],
    unique: true,
    supports: { label: true, align: true },
    labelPlaceholder: 'Description',
    options: [
      { key: 'show_heading', label: 'Show a heading over it', kind: 'switch', default: false },
      {
        key: 'collapsible',
        label: 'Fold it away',
        hint: 'A long description opens on a click rather than filling the page.',
        kind: 'switch',
        default: false,
      },
    ],
  },
  {
    type: 'product_specs',
    label: 'Specifications',
    description: "The product's own fields - materials, dimensions, whatever you record.",
    group: 'detail',
    anchors: ['product_details'],
    unique: true,
    supports: { label: true, align: true },
    labelPlaceholder: 'Details',
  },
  {
    type: 'product_shipping_info',
    label: 'Delivery and returns',
    description: 'What you promise about getting it there, and about sending it back.',
    group: 'detail',
    anchors: ['product_details'],
    supports: { label: true, body: true, background: true },
    labelPlaceholder: 'Delivery and returns',
    defaults: { label: 'Delivery and returns' },
    options: [
      { key: 'collapsible', label: 'Fold it away', kind: 'switch', default: true },
      {
        key: 'icon',
        label: 'Icon',
        kind: 'select',
        choices: [
          { value: 'none', label: 'None' },
          { value: 'truck', label: 'Delivery van' },
          { value: 'return', label: 'Returns arrow' },
          { value: 'shield', label: 'Shield' },
          { value: 'clock', label: 'Clock' },
        ],
        default: 'truck',
      },
    ],
  },
  {
    type: 'product_payment',
    label: 'Payment and security',
    description: 'What you take, and the line about what happens to a card.',
    group: 'money',
    anchors: ['product_details'],
    unique: true,
    supports: { label: true, body: true, background: true },
    labelPlaceholder: 'Payment & security',
    defaults: {
      label: 'Payment & security',
      body: 'Your payment is processed securely. We never see your card number, and nothing about it is stored on this shop.',
    },
    options: [
      {
        key: 'methods',
        label: 'What you take',
        hint: 'Separated by commas. Each becomes one chip. Names rather than logos - a scheme’s mark is its own trademark.',
        kind: 'text',
        placeholder: 'Visa, Mastercard, UPI',
        default: 'Visa, Mastercard, Amex, RuPay, UPI, Cash on delivery',
      },
      { key: 'collapsible', label: 'Fold it away', kind: 'switch', default: false },
    ],
  },
  {
    type: 'product_share',
    label: 'Share',
    description: 'Ways for a shopper to pass this page on.',
    group: 'action',
    anchors: ['product_details'],
    unique: true,
    supports: { label: true, align: true },
    labelPlaceholder: 'Share',
    options: [
      { key: 'facebook', label: 'Facebook', kind: 'switch', default: true },
      { key: 'x', label: 'X', kind: 'switch', default: true },
      { key: 'whatsapp', label: 'WhatsApp', kind: 'switch', default: true },
      { key: 'copy_link', label: 'A link to this page', kind: 'switch', default: true },
    ],
  },
  {
    type: 'product_related',
    label: 'Related products',
    description: 'Similar things, and what else sold alongside this.',
    group: 'discovery',
    anchors: ['product_details'],
    regions: ['below', 'main'],
    unique: true,
    supports: { background: true },
  },
  {
    type: 'product_recommendations',
    label: 'Recommended for you',
    description: "Chosen from this shopper's own history.",
    group: 'discovery',
    anchors: ['product_details'],
    regions: ['below', 'main'],
    unique: true,
    supports: { background: true },
  },
  {
    type: 'product_comparison',
    label: 'Compare with similar',
    description: 'This product beside its neighbours: price, options, maker, availability.',
    group: 'discovery',
    anchors: ['product_details'],
    regions: ['below', 'main'],
    unique: true,
    supports: { label: true, background: true },
    labelPlaceholder: 'Compare with similar',
    options: [
      {
        key: 'columns',
        label: 'Products in the table',
        hint: 'Including this one. A table a shopper scrolls sideways twice is a catalogue.',
        kind: 'number',
        min: 2,
        max: 4,
        default: 4,
      },
      { key: 'show_description', label: 'The summary row', kind: 'switch', default: true },
      { key: 'show_options', label: 'The options row', kind: 'switch', default: true },
      { key: 'show_brand', label: 'The maker row', kind: 'switch', default: true },
      { key: 'show_availability', label: 'The availability row', kind: 'switch', default: true },
    ],
  },

  {
    type: 'product_accordion',
    label: 'Accordion',
    description: 'Folding panels - ingredients, how to use, care - each opened with a tap.',
    group: 'detail',
    anchors: ['product_details'],
    // The buy column first, where ingredients and care are read beside the
    // price; under the page or beside the pictures where a theme has room.
    regions: ['main', 'below', 'gallery'],
    // Not unique: one accordion of facts under the buy box and another of
    // care notes under the page is a reasonable page.
    supports: { body: true },
    defaults: { body: '# How to use\nWrite the steps here.\n\n# Storage\nKeep in a cool, dry place.' },
    options: [
      {
        key: 'include_specs',
        label: "Include the product's specifications",
        hint: 'Each specification with a value becomes a panel, before your own.',
        kind: 'switch',
        default: true,
      },
      { key: 'first_open', label: 'Open the first panel', kind: 'switch', default: false },
      {
        key: 'single_open',
        label: 'One panel open at a time',
        hint: 'Opening a panel closes the one that was open.',
        kind: 'switch',
        default: true,
      },
    ],
  },
  {
    type: 'product_delivery_estimate',
    label: 'Delivery date check',
    description: 'A PIN code box that answers when it would arrive, and whether cash on delivery is offered there.',
    group: 'detail',
    anchors: ['product_details'],
    regions: ['main', 'below', 'gallery'],
    unique: true,
    supports: { label: true, background: true },
    labelPlaceholder: 'Check delivery',
    options: [
      { key: 'show_cod', label: 'Say whether cash on delivery is offered', kind: 'switch', default: true },
      {
        key: 'min_days',
        label: 'Fewest days to deliver',
        hint: 'Days to pack plus days on the road. Used only where your delivery settings give no date.',
        kind: 'number',
        min: 1,
        max: 30,
        default: 3,
      },
      {
        key: 'max_days',
        label: 'Most days to deliver',
        hint: 'The far end of the same range.',
        kind: 'number',
        min: 1,
        max: 45,
        default: 7,
      },
    ],
  },
  {
    type: 'product_stock_bar',
    label: 'Low-stock bar',
    description: '"Only 4 left", with a bar that empties - shown only when stock runs low.',
    group: 'detail',
    anchors: ['product_details'],
    regions: ['main', 'gallery', 'below'],
    unique: true,
    supports: TEXT_ONLY,
    options: [
      {
        key: 'threshold',
        label: 'Show it at or below',
        hint: 'Pieces left. Above this, and on products whose stock you do not track, it shows nothing.',
        kind: 'number',
        min: 1,
        max: 50,
        default: 10,
      },
      {
        key: 'style',
        label: 'Shown as',
        kind: 'select',
        choices: [
          { value: 'bar', label: 'Words over a bar' },
          { value: 'text', label: 'Words only' },
        ],
        default: 'bar',
      },
    ],
  },
  {
    type: 'product_offers',
    label: 'Offers and codes',
    description: 'Your discount codes beside the price, each with a button that copies it.',
    group: 'money',
    anchors: ['product_details'],
    regions: ['main', 'below', 'gallery'],
    unique: true,
    supports: { label: true, body: true, background: true },
    labelPlaceholder: 'Offers',
    defaults: { body: 'SAVE10: 10% off your first order\nFREESHIP: Free delivery on orders over ₹999' },
    options: [{ key: 'show_copy', label: 'A copy button beside each code', kind: 'switch', default: true }],
  },
  {
    type: 'product_sticky_cart',
    label: 'Sticky add-to-cart bar',
    description: 'A bar along the bottom of the screen with the name, the price and the button, once the main button scrolls away.',
    group: 'action',
    anchors: ['product_details'],
    // Wherever it is put it draws itself pinned to the screen, so the region
    // only decides where it sits in the tree.
    regions: ['main', 'below', 'gallery', 'top'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Add to cart',
    options: [
      {
        key: 'show_on',
        label: 'Show it on',
        kind: 'select',
        choices: [
          { value: 'mobile', label: 'Phones only' },
          { value: 'all', label: 'Every screen' },
        ],
        default: 'mobile',
      },
      { key: 'show_image', label: 'Show the first picture', kind: 'switch', default: true },
    ],
  },
  {
    type: 'product_size_chart',
    label: 'Size chart',
    description: 'A "Size chart" link that opens your measurements table, a picture of it, or both.',
    group: 'detail',
    anchors: ['product_details'],
    regions: ['main', 'below', 'gallery'],
    unique: true,
    supports: { label: true, body: true, align: true },
    labelPlaceholder: 'Size chart',
    defaults: {
      body: 'Size | Chest (in) | Length (in)\nS | 36 | 27\nM | 38 | 28\nL | 40 | 29\nXL | 42 | 30',
    },
    options: [
      { key: 'image_url', label: 'Picture of the chart', kind: 'text', default: '' },
      {
        key: 'note',
        label: 'Line under the table',
        kind: 'text',
        placeholder: 'Measurements are of the garment, in inches.',
        default: '',
      },
    ],
  },
  {
    type: 'product_bundle',
    label: 'Frequently bought together',
    description: 'This product and the ones you bundle with it, joined with +, the price of the set and one "Add all to cart" button.',
    group: 'money',
    anchors: ['product_details'],
    regions: ['below', 'main'],
    unique: true,
    supports: { label: true, background: true },
    labelPlaceholder: 'Frequently bought together',
    options: [
      {
        key: 'show_checkboxes',
        label: 'Let them untick items',
        hint: 'The bundle discount still needs every item in the cart.',
        kind: 'switch',
        default: true,
      },
      {
        key: 'layout',
        label: 'Laid out as',
        kind: 'select',
        choices: [
          { value: 'row', label: 'Pictures in a row' },
          { value: 'list', label: 'A list' },
        ],
        default: 'row',
      },
    ],
  },
  {
    type: 'product_qa',
    label: 'Questions and answers',
    description: "Customers' questions about the product with your answers, and a form to ask one.",
    group: 'discovery',
    anchors: ['product_details'],
    regions: ['below', 'main'],
    unique: true,
    supports: { label: true, align: true },
    labelPlaceholder: 'Questions and answers',
    options: [
      {
        key: 'page_size',
        label: 'Questions shown at first',
        hint: 'More load with a button underneath.',
        kind: 'number',
        min: 3,
        max: 20,
        default: 5,
      },
      { key: 'allow_questions', label: 'Let customers ask questions', kind: 'switch', default: true },
      {
        key: 'show_ask_button',
        label: 'Keep the form behind an "Ask a question" button',
        hint: 'Off shows the form open under the questions.',
        kind: 'switch',
        default: true,
      },
    ],
  },
  {
    type: 'product_notify_me',
    label: 'Notify me when available',
    description: 'An email box shown only while the chosen option is sold out, so the customer hears when it is back.',
    group: 'action',
    anchors: ['product_details'],
    regions: ['main'],
    unique: true,
    supports: { label: true, align: true, width: true },
    labelPlaceholder: 'Notify me',
    options: [
      {
        key: 'hide_add_to_cart',
        label: 'Hide the add-to-cart button while sold out',
        hint: 'Off keeps the greyed-out button above this box.',
        kind: 'switch',
        default: true,
      },
    ],
  },
  {
    type: 'product_seller_info',
    label: 'Seller information',
    description:
      'A bordered panel with your legal name, address, GSTIN or FSSAI number and customer care, from Settings > Business. Shows only once you switch on "Show seller details on product pages".',
    group: 'detail',
    anchors: ['product_details'],
    regions: ['main', 'gallery'],
    unique: true,
    supports: { label: true, align: true, width: true },
    labelPlaceholder: 'Seller information',
  },

  // --- The cart -----------------------------------------------------------
  {
    type: 'cart_heading',
    label: 'Cart heading',
    description: 'The words at the top of the basket.',
    group: 'essential',
    anchors: ['cart_lines'],
    regions: ['top', 'main', 'summary'],
    unique: true,
    supports: TEXT_ONLY,
    labelPlaceholder: 'Your cart',
  },
  {
    type: 'cart_items',
    label: 'Cart items',
    description: 'The lines: picture, name, quantity and line total.',
    group: 'essential',
    anchors: ['cart_lines'],
    regions: ['main'],
    unique: true,
    required: true,
    supports: {},
    options: [
      { key: 'show_image', label: 'Show pictures', kind: 'switch', default: true },
      { key: 'show_options', label: 'Show the variant under the name', kind: 'switch', default: true },
      { key: 'allow_quantity', label: 'Let them change the quantity', kind: 'switch', default: true },
      { key: 'allow_remove', label: 'Let them remove a line', kind: 'switch', default: true },
    ],
  },
  {
    type: 'cart_continue_link',
    label: 'Keep shopping link',
    description: 'The way back into the shop from an empty basket.',
    group: 'action',
    anchors: ['cart_lines'],
    regions: ['main', 'top', 'below'],
    unique: true,
    supports: BUTTON,
    labelPlaceholder: 'Continue shopping',
  },
  {
    type: 'cart_summary_heading',
    label: 'Summary heading',
    description: 'The words over the totals.',
    group: 'money',
    anchors: ['cart_lines'],
    regions: ['summary'],
    unique: true,
    supports: TEXT_ONLY,
    labelPlaceholder: 'Order summary',
  },
  {
    type: 'cart_subtotal',
    label: 'Subtotal',
    description: 'What the lines come to before anything else.',
    group: 'money',
    anchors: ['cart_lines'],
    regions: ['summary'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Subtotal',
  },
  {
    type: 'cart_discount',
    label: 'Discount row',
    description: 'What a code took off. Shows only when one is applied.',
    group: 'money',
    anchors: ['cart_lines'],
    regions: ['summary'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Discount',
  },
  {
    type: 'cart_tax',
    label: 'Tax row',
    description: 'The tax on the order.',
    group: 'money',
    anchors: ['cart_lines'],
    regions: ['summary'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Tax',
  },
  {
    type: 'cart_shipping',
    label: 'Delivery row',
    description: 'What delivery costs, once it is known.',
    group: 'money',
    anchors: ['cart_lines'],
    regions: ['summary'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Delivery',
  },
  {
    type: 'cart_total',
    label: 'Total',
    description: 'The number the shopper is agreeing to.',
    group: 'money',
    anchors: ['cart_lines'],
    regions: ['summary'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Total',
  },
  {
    type: 'cart_coupon',
    label: 'Discount code',
    description: 'The box a shopper types a code into.',
    group: 'money',
    anchors: ['cart_lines'],
    regions: ['summary', 'main'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Discount code',
    options: [
      { key: 'button_label', label: 'Button', kind: 'text', placeholder: 'Apply', default: '' },
    ],
  },
  {
    type: 'cart_checkout_button',
    label: 'Checkout button',
    description: 'The way out of the basket and into the till.',
    group: 'action',
    anchors: ['cart_lines'],
    regions: ['summary', 'main'],
    unique: true,
    supports: BUTTON,
    labelPlaceholder: 'Checkout',
  },
  {
    type: 'cart_recommendations',
    label: 'Recommended for you',
    description: 'What else this shopper might still be looking for.',
    group: 'discovery',
    anchors: ['cart_lines'],
    regions: ['below', 'main'],
    unique: true,
    supports: { background: true },
  },
  {
    type: 'cart_note',
    label: 'Order notes',
    description: 'A box where the shopper writes instructions for the order.',
    group: 'detail',
    anchors: ['cart_lines'],
    regions: ['main', 'summary'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Order instructions',
    options: [
      {
        key: 'placeholder',
        label: 'Hint inside the box',
        kind: 'text',
        placeholder: 'Delivery or gift notes',
        default: '',
      },
      {
        key: 'max_length',
        label: 'Longest note',
        hint: 'In characters, up to 1000.',
        kind: 'number',
        min: 50,
        max: 1000,
        default: 500,
      },
    ],
  },

  // --- The till -----------------------------------------------------------
  {
    type: 'checkout_heading',
    label: 'Checkout heading',
    description: 'The words at the top of the till.',
    group: 'essential',
    anchors: ['checkout_form'],
    regions: ['top', 'main'],
    unique: true,
    supports: TEXT_ONLY,
    labelPlaceholder: 'Checkout',
  },
  {
    type: 'checkout_fields',
    label: 'Checkout form',
    description: 'Address, delivery and payment. The fields themselves are fixed.',
    group: 'essential',
    anchors: ['checkout_form'],
    regions: ['main'],
    unique: true,
    required: true,
    supports: {},
  },
  {
    type: 'checkout_summary_heading',
    label: 'Summary heading',
    description: 'The words over what is being bought.',
    group: 'money',
    anchors: ['checkout_form'],
    regions: ['summary'],
    unique: true,
    supports: TEXT_ONLY,
    labelPlaceholder: 'Order summary',
  },
  {
    type: 'checkout_summary_items',
    label: 'Order lines',
    description: 'What is in the order, listed beside the form.',
    group: 'essential',
    anchors: ['checkout_form'],
    regions: ['summary'],
    unique: true,
    supports: {},
    options: [{ key: 'show_image', label: 'Show pictures', kind: 'switch', default: true }],
  },
  {
    type: 'checkout_subtotal',
    label: 'Subtotal',
    description: 'What the lines come to.',
    group: 'money',
    anchors: ['checkout_form'],
    regions: ['summary'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Subtotal',
  },
  {
    type: 'checkout_discount',
    label: 'Discount row',
    description: 'What a code took off.',
    group: 'money',
    anchors: ['checkout_form'],
    regions: ['summary'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Discount',
  },
  {
    type: 'checkout_tax',
    label: 'Tax row',
    description: 'The tax on the order.',
    group: 'money',
    anchors: ['checkout_form'],
    regions: ['summary'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Tax',
  },
  {
    type: 'checkout_shipping',
    label: 'Delivery row',
    description: 'What delivery costs.',
    group: 'money',
    anchors: ['checkout_form'],
    regions: ['summary'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Delivery',
  },
  {
    type: 'checkout_total',
    label: 'Total',
    description: 'The number being charged.',
    group: 'money',
    anchors: ['checkout_form'],
    regions: ['summary'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Total',
  },
  {
    type: 'checkout_coupon',
    label: 'Discount code',
    description: 'The box a shopper types a code into at the till.',
    group: 'money',
    anchors: ['checkout_form'],
    regions: ['summary', 'main'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Discount code',
  },
  {
    type: 'checkout_gift_card',
    label: 'Gift card',
    description: 'The box a shopper pays with a gift card from.',
    group: 'money',
    anchors: ['checkout_form'],
    regions: ['summary', 'main'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Gift card',
  },
  {
    type: 'checkout_place_order',
    label: 'Place order button',
    description: 'The last button in the shop.',
    group: 'action',
    anchors: ['checkout_form'],
    regions: ['summary', 'main'],
    unique: true,
    required: true,
    supports: BUTTON,
    labelPlaceholder: 'Place order',
  },

  // --- The catalog --------------------------------------------------------
  {
    type: 'listing_heading',
    label: 'Catalog heading',
    description: 'The words at the top. A category being browsed keeps its own name.',
    group: 'essential',
    anchors: ['product_list'],
    regions: ['top', 'main', 'below'],
    unique: true,
    supports: TEXT_ONLY,
    labelPlaceholder: 'All products',
  },
  {
    type: 'listing_refine_link',
    label: 'Refine link',
    description: 'The way from browsing into searching.',
    group: 'action',
    anchors: ['product_list'],
    regions: ['top', 'main', 'below'],
    unique: true,
    supports: BUTTON,
    labelPlaceholder: 'Refine',
  },
  {
    // The shelf's own picture and description, over the products. Its words
    // and picture are the category's until the merchant writes their own,
    // which is also what puts a banner on the unfiltered catalog.
    type: 'listing_banner',
    label: 'Category banner',
    description:
      "The category's own picture and description above the products - or words and a picture of your own.",
    group: 'discovery',
    anchors: ['product_list'],
    regions: ['top', 'main'],
    unique: true,
    supports: { label: true, body: true, align: true },
    labelPlaceholder: 'The category name',
    options: [
      {
        key: 'layout',
        label: 'Layout',
        kind: 'select',
        choices: [
          { value: 'split', label: 'Picture beside the words' },
          { value: 'overlay', label: 'Words over the picture' },
          { value: 'text', label: 'Words only' },
        ],
        default: 'split',
      },
      {
        key: 'height',
        label: 'Height',
        kind: 'select',
        choices: [
          { value: 'sm', label: 'Short' },
          { value: 'md', label: 'Medium' },
          { value: 'lg', label: 'Tall' },
        ],
        default: 'md',
      },
      {
        key: 'image_side',
        label: 'Picture on the',
        hint: 'For the picture-beside-the-words layout.',
        kind: 'select',
        choices: [
          { value: 'left', label: 'Left' },
          { value: 'right', label: 'Right' },
        ],
        default: 'right',
      },
      { key: 'image_url', label: 'Picture of your own', kind: 'text', default: '' },
    ],
  },
  {
    type: 'listing_filters',
    label: 'Filters',
    description:
      'The shelves as chips, a sidebar or an inline list - or the full filters: price, brand, size, colour and stock, with counts.',
    group: 'essential',
    anchors: ['product_list'],
    regions: ['top', 'main'],
    unique: true,
    supports: {},
    options: [
      {
        key: 'mode',
        label: 'Filter by',
        hint: 'The full filters narrow the shelf being browsed, and fold into a drawer on a phone.',
        kind: 'select',
        choices: [
          { value: 'categories', label: 'Categories only' },
          { value: 'facets', label: 'Price, brand, size, colour and stock' },
        ],
        // What every catalog drew before the full filters came to it, so a
        // shop nobody has touched keeps its category links.
        default: 'categories',
      },
      {
        key: 'sidebar_style',
        label: 'Sidebar rows',
        hint: 'For a sidebar of categories: words only, or each category with its picture.',
        kind: 'select',
        choices: [
          { value: 'text', label: 'Words only' },
          { value: 'pictures', label: 'Picture and name' },
        ],
        default: 'text',
      },
    ],
  },
  {
    type: 'listing_sort',
    label: 'Sort',
    description: 'Newest, cheapest, by name - the same choice search offers.',
    group: 'essential',
    anchors: ['product_list'],
    regions: ['top', 'main'],
    unique: true,
    supports: {},
  },
  {
    type: 'listing_grid',
    label: 'Product grid',
    description: 'The products themselves.',
    group: 'essential',
    anchors: ['product_list'],
    regions: ['main', 'below'],
    unique: true,
    required: true,
    supports: {},
  },
  {
    // Filed under main, it is drawn by the grid, inside its flow, rather than
    // where it stands in the tree - see `listingPromo.ts`. Filed under top, it
    // is a small offer card right under the heading. A part of its own so it
    // can be hidden, kept off a phone or deleted without touching the grid.
    type: 'listing_promo_tile',
    label: 'Promo tile in the grid',
    description: 'An advert standing among the products on the first page - an offer, a new range, a sale.',
    group: 'discovery',
    anchors: ['product_list'],
    regions: ['main', 'top'],
    unique: true,
    supports: {},
    options: [
      {
        key: 'position',
        label: 'Place in the grid',
        hint: 'Counted from the first product. A page with fewer products leaves it out.',
        kind: 'number',
        min: 1,
        max: 24,
        default: 4,
      },
      {
        key: 'span',
        label: 'Width',
        kind: 'select',
        choices: [
          { value: '1', label: 'One product wide' },
          { value: '2', label: 'Two products wide' },
        ],
        default: '1',
      },
      { key: 'image_url', label: 'Picture', kind: 'text', default: '' },
      {
        key: 'color',
        label: 'Colour without a picture',
        kind: 'select',
        choices: [
          { value: 'primary', label: 'Brand colour' },
          { value: 'surface', label: 'Soft tint' },
          { value: 'ink', label: 'Text colour' },
          { value: 'accent', label: 'Highlight colour' },
        ],
        default: 'primary',
      },
      { key: 'eyebrow', label: 'Small line above', kind: 'text', placeholder: 'Limited time', default: '' },
      // Blank heading and button words are the shop's own, translated with
      // the rest of the storefront - see `listing.promo_heading`.
      {
        key: 'heading',
        label: 'Heading',
        hint: "Blank shows the shop's own words, in the shopper's language.",
        kind: 'text',
        placeholder: 'New arrivals',
        default: '',
      },
      // The offer card under the heading has room for a line under its heading.
      { key: 'text', label: 'Short line', kind: 'text', placeholder: 'On your first order', default: '' },
      { key: 'cta_label', label: 'Button words', kind: 'text', placeholder: 'Shop now', default: '' },
      { key: 'href', label: 'Links to', kind: 'text', placeholder: '/products', default: '' },
    ],
  },
  {
    type: 'listing_pagination',
    label: 'Pages',
    description: 'Previous, next, and where the shopper is.',
    group: 'essential',
    anchors: ['product_list'],
    regions: ['main', 'below'],
    unique: true,
    supports: {},
  },

  // --- The category index -------------------------------------------------
  {
    type: 'category_heading',
    label: 'Heading',
    description: 'The words at the top of the shelves.',
    group: 'essential',
    anchors: ['category_list'],
    regions: ['top', 'main', 'below'],
    unique: true,
    supports: TEXT_ONLY,
    labelPlaceholder: 'Shop by category',
  },
  {
    type: 'category_grid',
    label: 'Category grid',
    description: 'The shelves themselves.',
    group: 'essential',
    anchors: ['category_list'],
    regions: ['main', 'below'],
    unique: true,
    required: true,
    supports: {},
  },

  // --- Search -------------------------------------------------------------
  {
    type: 'search_heading',
    label: 'Search heading',
    description: 'The words at the top of the results.',
    group: 'essential',
    anchors: ['search_results'],
    regions: ['top', 'main'],
    unique: true,
    supports: TEXT_ONLY,
    labelPlaceholder: 'Search',
  },
  {
    type: 'search_count',
    label: 'Result count',
    description: 'How many matches, and what was searched for.',
    group: 'essential',
    anchors: ['search_results'],
    regions: ['main', 'top'],
    unique: true,
    supports: TEXT_ONLY,
  },
  {
    type: 'search_facets',
    label: 'Facet panel',
    description: 'Price, brand, options - what a shopper narrows results by.',
    group: 'essential',
    anchors: ['search_results'],
    regions: ['facets'],
    unique: true,
    supports: {},
  },
  {
    type: 'search_sort',
    label: 'Sort',
    description: 'Newest, cheapest, by name.',
    group: 'essential',
    anchors: ['search_results'],
    regions: ['main', 'top', 'facets'],
    unique: true,
    supports: { label: true },
    labelPlaceholder: 'Sort by',
  },
  {
    type: 'search_grid',
    label: 'Results grid',
    description: 'The matches themselves.',
    group: 'essential',
    anchors: ['search_results'],
    regions: ['main'],
    unique: true,
    required: true,
    supports: {},
  },
  {
    type: 'search_pagination',
    label: 'Pages',
    description: 'Previous, next, and where the shopper is.',
    group: 'essential',
    anchors: ['search_results'],
    regions: ['main', 'below'],
    unique: true,
    supports: {},
  },
];

const BY_TYPE = new Map(PART_LIBRARY.map((spec) => [spec.type, spec]));

/**
 * One part's description.
 *
 * Falls back rather than throwing, for the same reason `sectionSpec` does: a
 * document naming a part this build retired should still render the page.
 */
export function partSpec(type: PartType): PartSpec {
  return BY_TYPE.get(type) ?? PART_LIBRARY[0];
}

/** Whether a part belongs on a page. */
export function partBelongsTo(spec: PartSpec, anchor: AnchorSectionType): boolean {
  return spec.anchors === 'any' || spec.anchors.includes(anchor);
}

/** Every part this page has, in library order. */
export function partsForAnchor(anchor: AnchorSectionType): readonly PartSpec[] {
  return PART_LIBRARY.filter((spec) => partBelongsTo(spec, anchor));
}

/** The regions one part may stand in on one page, narrowed to the ones that exist. */
export function partRegions(spec: PartSpec, anchor: AnchorSectionType): readonly RegionSpec[] {
  const regions = anchorRegions(anchor);
  if (!spec.regions) return regions;
  const allowed = regions.filter((region) => spec.regions?.includes(region.id));
  return allowed.length > 0 ? allowed : regions;
}

// --- Building and reading -------------------------------------------------
/** The options a part arrives with, from what its spec declared. */
export function defaultOptions(type: PartType): Record<string, PartOptionValue> {
  return Object.fromEntries((partSpec(type).options ?? []).map((each) => [each.key, each.default]));
}

/**
 * A new part of one type, standing in one region.
 *
 * The region is passed rather than derived so the library can add a part
 * exactly where the merchant was looking, and defaulted to where the part
 * belongs when it is not.
 */
export function createPart(type: PartType, region?: string): PagePart {
  const spec = partSpec(type);
  return pagePartSchema.parse({
    id: newElementId(),
    type,
    region: region ?? spec.regions?.[0] ?? '',
    options: defaultOptions(type),
    ...spec.defaults,
  });
}

/** The same part, with an identity of its own. */
export function copyPart(part: PagePart): PagePart {
  return { ...part, id: newElementId(), options: { ...part.options } };
}

/**
 * One option's value, read through the spec that declared it.
 *
 * A value of the wrong kind - a string where a switch was declared, a number
 * outside the range - falls back to the default rather than reaching the
 * renderer. That is what makes `options` safe to carry as an open map: the
 * schema keeps it scalar, and this keeps it meaningful.
 */
export function partOption(part: PagePart, key: string): PartOptionValue {
  const declared = (partSpec(part.type).options ?? []).find((each) => each.key === key);
  if (!declared) return '';
  const value = part.options?.[key];
  switch (declared.kind) {
    case 'switch':
      return typeof value === 'boolean' ? value : declared.default;
    case 'number': {
      const numeric = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(numeric) && numeric >= declared.min && numeric <= declared.max
        ? numeric
        : declared.default;
    }
    case 'select':
      return typeof value === 'string' && declared.choices.some((choice) => choice.value === value)
        ? value
        : declared.default;
    case 'text':
      return typeof value === 'string' ? value : declared.default;
  }
}

export function partFlag(part: PagePart, key: string): boolean {
  return partOption(part, key) === true;
}

export function partChoice(part: PagePart, key: string): string {
  const value = partOption(part, key);
  return typeof value === 'string' ? value : '';
}

export function partNumber(part: PagePart, key: string): number {
  const value = partOption(part, key);
  return typeof value === 'number' ? value : 0;
}

/** What the tree and the inspector call one part: the merchant's words, or the spec's. */
export function partTitle(part: PagePart): string {
  const spec = partSpec(part.type);
  const own = part.label.trim();
  return own && spec.supports?.label ? own : spec.label;
}

/**
 * The region a part actually stands in.
 *
 * A part whose region names nothing on this page falls into the main one rather
 * than disappearing - which is what a document written before a region was
 * renamed, or copied from a page with a different shape, looks like.
 */
export function resolvedRegion(part: PagePart, anchor: AnchorSectionType): string {
  const known = anchorRegions(anchor).some((each) => each.id === part.region);
  return known ? part.region : defaultRegion(anchor);
}

/** The parts of one region, in order. */
export function partsInRegion(
  parts: readonly PagePart[],
  anchor: AnchorSectionType,
  region: string,
): PagePart[] {
  return parts.filter((part) => resolvedRegion(part, anchor) === region);
}

/**
 * The whole list, with one region's components put in a new order.
 *
 * The document is one flat array and the tree is a list per region, so a drag
 * inside the buy column has to be written back without disturbing the gallery
 * column's order. Done by walking the original and handing out the reordered
 * ones in turn wherever a component of that region stood: the positions of
 * everything else are untouched, which is what makes a drag in one region cost
 * nothing in another.
 */
export function reorderRegion(
  parts: readonly PagePart[],
  anchor: AnchorSectionType,
  region: string,
  ordered: readonly PagePart[],
): PagePart[] {
  let next = 0;
  return parts.map((part) =>
    resolvedRegion(part, anchor) === region ? (ordered[next++] ?? part) : part,
  );
}

/**
 * The list with one component moved into another region.
 *
 * It lands at the end of the region it is moving to, which is the only answer
 * that is never surprising: a component dropped into the middle of a column the
 * merchant is not looking at would move something they did not ask to move.
 */
export function movePartToRegion(
  parts: readonly PagePart[],
  anchor: AnchorSectionType,
  id: string,
  region: string,
): PagePart[] {
  const moving = parts.find((part) => part.id === id);
  if (!moving) return [...parts];

  const rest = parts.filter((part) => part.id !== id);
  const moved = { ...moving, region };
  let at = rest.length;
  for (let index = rest.length - 1; index >= 0; index -= 1) {
    if (resolvedRegion(rest[index], anchor) === region) {
      at = index + 1;
      break;
    }
  }
  return [...rest.slice(0, at), moved, ...rest.slice(at)];
}

/**
 * The ceiling the server also enforces. Generous, because a product page with
 * specifications, promises and two rails under it is a real page - and small
 * enough that a document cannot become a way to make a shopper's browser hang.
 */
export const MAX_PARTS_PER_ANCHOR = 40;

/**
 * Whether one more of this part may be added to a page.
 *
 * Two rules: a unique part is offered until the page has one, and a part the
 * theme has left off its list is never offered at all.
 */
export function isPartAddable(
  spec: PartSpec,
  anchor: AnchorSectionType,
  present: readonly PagePart[],
  available?: readonly PartType[],
): boolean {
  if (!partBelongsTo(spec, anchor)) return false;
  if (available && !available.includes(spec.type)) return false;
  if (spec.unique && present.some((part) => part.type === spec.type)) return false;
  return true;
}

/**
 * Parses a saved list, dropping what this build cannot render.
 *
 * The same bargain `parseSections` strikes: a part with an unknown type or a
 * broken field is left out, so a document written by a newer deploy degrades to
 * a shorter page rather than a broken one.
 */
export function parseParts(value: unknown): PagePart[] {
  if (!Array.isArray(value)) return [];
  const parts: PagePart[] = [];
  for (const candidate of value) {
    const result = pagePartSchema.safeParse(candidate);
    if (result.success) parts.push(result.data);
  }
  return parts.slice(0, MAX_PARTS_PER_ANCHOR);
}

// --- Words the merchant writes in a body -----------------------------------
/**
 * The icons a highlight line may name in the "icons" style, as `icon: Label`.
 * Mirrors the analyser's list in `app/services/store/theme_builder.py`.
 */
export const HIGHLIGHT_ICONS = [
  'leaf',
  'droplet',
  'flask',
  'heart',
  'sprout',
  'sun',
  'award',
  'shield',
  'check',
  'star',
  'wheat',
  'recycle',
  'flame',
  'snowflake',
] as const;
export type HighlightIcon = (typeof HIGHLIGHT_ICONS)[number];

/**
 * One highlight line read as an icon and a label: `leaf: Organic` is a leaf
 * over "Organic". A line with no icon, or one this build does not draw, keeps
 * its words and gets a tick - so a merchant's typo costs a picture, never the
 * claim. A colon inside the words (`Ratio: 1:2`) stays in them.
 */
export function parseHighlightLine(line: string): { icon: HighlightIcon; label: string } {
  const match = line.match(/^\s*([a-z]+)\s*:\s*(.+)$/i);
  if (match) {
    const name = match[1].toLowerCase();
    if ((HIGHLIGHT_ICONS as readonly string[]).includes(name)) {
      return { icon: name as HighlightIcon, label: match[2].trim() };
    }
  }
  return { icon: 'check', label: line.trim() };
}

/** One panel of an accordion: a title and the words under it. */
export interface AccordionItem {
  title: string;
  body: string;
}

/**
 * An accordion's own panels, from its body: a line starting `# ` opens a panel
 * and names it, and the lines after it, up to the next, are its text. Words
 * before the first title have no panel to stand in and are dropped; a title
 * with no text under it is still a panel, since its name may be the point.
 */
export function parseAccordionBody(body: string): AccordionItem[] {
  const items: AccordionItem[] = [];
  let current: { title: string; lines: string[] } | null = null;
  const close = () => {
    if (current) items.push({ title: current.title, body: current.lines.join('\n').trim() });
  };
  for (const line of body.split(/\r?\n/)) {
    const heading = line.match(/^#\s+(.+)$/);
    if (heading) {
      close();
      current = { title: heading[1].trim(), lines: [] };
    } else if (current) {
      current.lines.push(line);
    }
  }
  close();
  return items;
}

/** One line of an offers body: the code to type, and what it gives. */
export interface OfferLine {
  /** Blank for a line that is an offer with no code - "Free gift over ₹2,000". */
  code: string;
  description: string;
}

/**
 * An offers part's lines, from its body: `SAVE10: 10% off your first order` is
 * the code SAVE10 and what it gives. A line whose first word is not a code -
 * spaces, punctuation - is an offer in words with nothing to copy, so a
 * merchant's "Free delivery: on every order" never grows a Copy button.
 */
export function parseOfferLines(body: string): OfferLine[] {
  const offers: OfferLine[] = [];
  for (const raw of body.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const match = line.match(/^([A-Za-z0-9][A-Za-z0-9_-]{1,29})\s*:\s*(.+)$/);
    offers.push(match ? { code: match[1].toUpperCase(), description: match[2].trim() } : { code: '', description: line });
  }
  return offers.slice(0, 12);
}

/**
 * A size chart's table, from its body: one row per line, cells split on `|`,
 * the first row the header. Blank lines are dropped, and every row is padded
 * or cut to the header's width so a typo cannot push a cell off the table.
 */
export function parseSizeChart(body: string): { header: string[]; rows: string[][] } | null {
  const lines = body
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) =>
      line
        .replace(/^\||\|$/g, '')
        .split('|')
        .map((cell) => cell.trim()),
    );
  if (lines.length === 0) return null;
  const [header, ...rest] = lines;
  const width = header.length;
  const rows = rest.map((row) => Array.from({ length: width }, (_, at) => row[at] ?? ''));
  return { header, rows };
}

/** Every anchor type, re-exported so callers need one import rather than two. */
export { ANCHOR_SECTION_TYPES };
export type { AnchorSectionType };
